import React, { useState, useEffect, useCallback, useRef } from 'react';
/* eslint-disable */
import { 
  FiX, 
  FiSave, 
  FiPlus, 
  FiBook, 
  FiCalendar, 
  FiFilter, 
  FiRefreshCw,
  FiCheckSquare,
  FiSquare, 
  FiChevronDown, 
  FiChevronUp, 
  FiUsers, 
  FiAlertCircle, 
  FiCopy, 
  FiEdit3, 
  FiCreditCard, 
  FiAward, 
  FiInfo, 
  FiPieChart, 
  FiAlertTriangle, 
  FiSearch,
  FiCheck,
  FiLayers,
  FiList // Added for term icon
} from 'react-icons/fi';
import { toast } from 'react-toastify'; 
import api from '../../api';
import { useDepartments } from './hooks/useDepartments';
import { useAcademicData } from './hooks/useAcademicData';

const ProgramSubjectMapping = ({
  tenantId,
  program,
  open,
  onClose,
  onMappingComplete,
  academicYearId,
  semesterId,
  termId, // NEW: Add termId prop
  existingMapping,
  isEditMode
}) => {
  // Academic context state - ADDED term support
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(academicYearId || '');
  const [selectedSemester, setSelectedSemester] = useState(semesterId || '');
  const [selectedTerm, setSelectedTerm] = useState(termId || ''); // NEW: Add selectedTerm state
  const [isSemesterBased, setIsSemesterBased] = useState(false);
  const [semesters, setSemesters] = useState([]);
  const [terms, setTerms] = useState([]); // NEW: Add terms state
  const [useParentContext, setUseParentContext] = useState(!!academicYearId);

  // Multi-department selection
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [activeDepartment, setActiveDepartment] = useState('');

  // Subjects state
  const [departmentSubjects, setDepartmentSubjects] = useState({});
  const [subjectsLoading, setSubjectsLoading] = useState({});
  const [subjectsError, setSubjectsError] = useState({});

  // Selected subjects for mapping
  const [selectedSubjects, setSelectedSubjects] = useState({});
  
  // Credit system state
  const [creditSystemEnabled, setCreditSystemEnabled] = useState(false);
  const [creditRules, setCreditRules] = useState(null);
  const [totalCredits, setTotalCredits] = useState(0);
  const [creditSummary, setCreditSummary] = useState(null);
  const [creditValidation, setCreditValidation] = useState({ valid: true, warnings: [] });
  const [validatingCredits, setValidatingCredits] = useState(false);
  const [autoFilledCredits, setAutoFilledCredits] = useState(false);

  // UI state
  const [expandedCategories, setExpandedCategories] = useState({});
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [autoExpandedCategories, setAutoExpandedCategories] = useState(false);

  // Custom hooks
  const { departments: allDepartments } = useDepartments(tenantId, open);
  const { academicYears } = useAcademicData(tenantId, false);

  // Refs to prevent duplicate fetching and initialization
  const isInitializingEdit = useRef(false);
  const hasInitializedEdit = useRef(false);
  const editModeInitialized = useRef(false);

  // Fetch subjects for a department with deduplication - UPDATED to handle term
  const fetchSubjectsForDepartment = useCallback(async (deptId) => {
    if (!tenantId || !selectedAcademicYear || !deptId) return [];
    if (departmentSubjects[deptId] && departmentSubjects[deptId].length > 0) {
      return departmentSubjects[deptId];
    }
    try {
      setSubjectsLoading(prev => ({ ...prev, [deptId]: true }));
      setSubjectsError(prev => ({ ...prev, [deptId]: null }));

      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = {
        tenantId,
        academicYearId: selectedAcademicYear,
        departmentId: deptId,
      };
      
      // Add appropriate context parameter
      if (isSemesterBased && selectedSemester) {
        params.semesterId = selectedSemester;
      } else if (!isSemesterBased && selectedTerm) {
        params.termId = selectedTerm;
      }

      const res = await api.get(
        '/api/subjects/class-manager/subjects/department/context',
        { headers, params }
      );
      const data = res.data?.data || {};
      const subj = data.subjects || {};
      const subjects = subj.all || [];

      // Deduplicate subjects by _id before setting state
      const uniqueSubjects = subjects.reduce((acc, subject) => {
        if (!acc.find(s => s._id === subject._id)) {
          acc.push(subject);
        }
        return acc;
      }, []);

      setDepartmentSubjects(prev => {
        const newVal = { ...prev, [deptId]: uniqueSubjects };
        return newVal;
      });

      return uniqueSubjects;
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Failed to fetch subjects';
      setSubjectsError(prev => ({
        ...prev,
        [deptId]: errorMessage
      }));
      setDepartmentSubjects(prev => ({
        ...prev,
        [deptId]: []
      }));
      return [];
    } finally {
      setSubjectsLoading(prev => ({ ...prev, [deptId]: false }));
    }
  }, [tenantId, selectedAcademicYear, isSemesterBased, selectedSemester, selectedTerm, departmentSubjects]);

// Initialize existing mapping - UPDATED to handle term
const initializeExistingMapping = useCallback(async () => {
  if (!existingMapping || !open || editModeInitialized.current) {
    console.log('[initEdit] Skipping: missing mapping, not open, or already initialized');
    return;
  }

  try {
    console.log('[initEdit] Starting initialization');
    isInitializingEdit.current = true;
    hasInitializedEdit.current = true;
    editModeInitialized.current = true;

    // Determine if this is semester-based from the mapping data itself
    const isSemesterBasedMapping = !!existingMapping.semesterId;
    console.log('[initEdit] isSemesterBased from mapping:', isSemesterBasedMapping);
    
    setIsSemesterBased(isSemesterBasedMapping);

    // Set academic context from existing mapping
    if (existingMapping.academicYearId) {
      const academicYearId = typeof existingMapping.academicYearId === 'object' 
        ? existingMapping.academicYearId._id 
        : existingMapping.academicYearId;
      setSelectedAcademicYear(academicYearId);
      console.log('[initEdit] Set selectedAcademicYear:', academicYearId);
    }
    
    // Handle semester OR term based on what exists in the mapping
    if (existingMapping.semesterId) {
      const semesterId = typeof existingMapping.semesterId === 'object'
        ? existingMapping.semesterId._id
        : existingMapping.semesterId;
      setSelectedSemester(semesterId);
      console.log('[initEdit] Set selectedSemester:', semesterId);
    } else if (existingMapping.termId) {
      const termId = typeof existingMapping.termId === 'object'
        ? existingMapping.termId._id
        : existingMapping.termId;
      setSelectedTerm(termId);
      console.log('[initEdit] Set selectedTerm:', termId);
    } else {
      setSelectedSemester('');
      setSelectedTerm('');
      console.log('[initEdit] No context in mapping, cleared');
    }
    
    setTotalCredits(existingMapping.totalCredits || 0);

    // Extract departments and selected subjects from existing mapping
    const existingDeptIds = existingMapping.departmentSubjects?.map(dept =>
      typeof dept.departmentId === 'object' ? dept.departmentId._id : dept.departmentId
    ) || [];
    console.log('[initEdit] existingDeptIds:', existingDeptIds);

    const existingSelectedSubjects = {};
    existingMapping.departmentSubjects?.forEach((deptSubj, idx) => {
      const deptId = typeof deptSubj.departmentId === 'object' ? deptSubj.departmentId._id : deptSubj.departmentId;
      console.log(`[initEdit] Dept #${idx} deptId:`, deptId, 'subjects:', deptSubj.subjects);
      if (deptId) {
        if (Array.isArray(deptSubj.subjects)) {
          // Check for null/undefined in subjects array
          const subjectIds = [];
          deptSubj.subjects.forEach((subj, subjIdx) => {
            if (!subj) {
              console.error(`[initEdit] Null/undefined subject at deptId=${deptId}, subjIdx=${subjIdx}:`, subj);
            } else if (!subj.subjectId) {
              console.error(`[initEdit] Missing subjectId at deptId=${deptId}, subjIdx=${subjIdx}:`, subj);
            }
            subjectIds.push(
              typeof subj.subjectId === 'object' && subj.subjectId !== null
                ? subj.subjectId._id
                : subj.subjectId
            );
          });
          existingSelectedSubjects[deptId] = subjectIds;
        } else {
          console.warn(`[initEdit] subjects is not an array for deptId=${deptId}:`, deptSubj.subjects);
          existingSelectedSubjects[deptId] = [];
        }
      } else {
        console.warn(`[initEdit] Missing deptId for departmentSubjects entry:`, deptSubj);
      }
    });
    console.log('[initEdit] existingSelectedSubjects:', existingSelectedSubjects);

    setDepartmentSubjects({});
    
    // Only wait for context if needed
    if (existingDeptIds.length > 0 && existingMapping.academicYearId) {
      if (isSemesterBasedMapping) {
        // For semester-based, we need the semester to be set
        if (!selectedSemester && !existingMapping.semesterId) {
          console.log('[initEdit] Semester-based but no semester, returning early');
          return;
        }
        // Fetch subjects for semester-based
        console.log('[initEdit] Fetching subjects for departments (semester-based):', existingDeptIds);
        await Promise.allSettled(
          existingDeptIds.map(deptId => fetchSubjectsForDepartment(deptId))
        );
        console.log('[initEdit] Finished fetching subjects for departments');
      } else {
        // This is term-based
        if (!selectedTerm && !existingMapping.termId) {
          console.log('[initEdit] Term-based but no term, returning early');
          return;
        }
        // Fetch subjects for term-based
        console.log('[initEdit] Fetching subjects for departments (term-based):', existingDeptIds);
        await Promise.allSettled(
          existingDeptIds.map(deptId => fetchSubjectsForDepartment(deptId))
        );
        console.log('[initEdit] Finished fetching subjects for departments');
      }
    }

    setSelectedDepartments(existingDeptIds);
    setSelectedSubjects(existingSelectedSubjects);

    if (existingDeptIds.length > 0) {
      setActiveDepartment(existingDeptIds[0]);
      console.log('[initEdit] Set activeDepartment:', existingDeptIds[0]);
    }

  } catch (error) {
    hasInitializedEdit.current = false;
    editModeInitialized.current = false;
    console.error('[initEdit] Error:', error);
  } finally {
    setTimeout(() => { 
      isInitializingEdit.current = false; 
      console.log('[initEdit] Done initializing');
    }, 100);
  }
}, [existingMapping, open, academicYears, fetchSubjectsForDepartment, selectedSemester, selectedTerm]);

  // Fetch all department subjects
  const fetchAllDepartmentSubjects = useCallback(async () => {
    if (!selectedDepartments.length || !selectedAcademicYear) {
      return;
    }
    try {
      const fetchPromises = selectedDepartments.map(deptId => 
        fetchSubjectsForDepartment(deptId)
      );
      await Promise.all(fetchPromises);
    } catch (error) {
      // Error handling is done in fetchSubjectsForDepartment
    }
  }, [selectedDepartments, selectedAcademicYear, fetchSubjectsForDepartment]);

  // Fetch credit system rules
  const fetchCreditSystemRules = useCallback(async () => {
    if (!tenantId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/grading-attendance-rules/${tenantId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        const rules = response.data.rules;
        setCreditRules(rules);
        const creditEnabled = rules.academicSystem?.creditSystemEnabled || false;
        setCreditSystemEnabled(creditEnabled);
        
        // Auto-fill credits for annual system
        if (creditEnabled && !isSemesterBased && !isEditMode) {
          const totalDegreeCredits = rules.academicSystem?.totalDegreeCredits || 140;
          setTotalCredits(totalDegreeCredits);
          setAutoFilledCredits(true);
        }
      }
    } catch (error) {
      setCreditSystemEnabled(false);
    }
  }, [tenantId, isSemesterBased, isEditMode]);

  // Fetch credit summary
  const fetchCreditSummary = useCallback(async () => {
    if (!tenantId || !program?._id || !selectedAcademicYear || !creditSystemEnabled) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/program-subject-mappings/credit-summary', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: {
          tenantId,
          programId: program._id,
          academicYearId: selectedAcademicYear
        }
      });
      
      if (response.data.success) {
        const summary = response.data.summary;
        if (summary.remainingCredits < 0) summary.remainingCredits = 0;
        if (summary.utilizationPercentage > 100) summary.utilizationPercentage = 100;
        setCreditSummary(summary);
      }
    } catch (error) {
      // Error handled silently
    }
  }, [tenantId, program?._id, selectedAcademicYear, creditSystemEnabled]);

  // Validate credits - UPDATED to include term
  const validateCredits = useCallback(async (credits) => {
    if (!tenantId || !program?._id || !selectedAcademicYear || !creditSystemEnabled) return;
    
    try {
      setValidatingCredits(true);
      const token = localStorage.getItem('token');
      const response = await api.post('/api/program-subject-mappings/validate-credits', {
        tenantId,
        programId: program._id,
        academicYearId: selectedAcademicYear,
        semesterId: selectedSemester,
        termId: selectedTerm, // ADDED termId
        credits: parseInt(credits) || 0,
        isEdit: isEditMode,
        existingMappingId: isEditMode ? existingMapping?._id : undefined
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        setCreditValidation(response.data.validation);
      }
    } catch (error) {
      const creditValue = parseInt(credits) || 0;
      const totalDegreeCredits = creditRules?.academicSystem?.totalDegreeCredits || 140;
      const warnings = [];
      
      if (creditValue > totalDegreeCredits) {
        warnings.push({
          type: 'advisory',
          message: `This allocation exceeds total degree credits (${totalDegreeCredits})`,
          icon: '💡'
        });
      }
      
      setCreditValidation({
        valid: true,
        warnings
      });
    } finally {
      setValidatingCredits(false);
    }
  }, [tenantId, program?._id, selectedAcademicYear, selectedSemester, selectedTerm, creditSystemEnabled, isEditMode, creditRules, existingMapping]);

  // Enhanced credit change handler
  const handleCreditChange = (value) => {
    const creditValue = parseInt(value) || 0;
    setTotalCredits(creditValue);
    if (creditValue !== 0) setAutoFilledCredits(false);
    if (creditValue > 0) {
      validateCredits(creditValue);
    } else {
      setCreditValidation({ valid: true, warnings: [] });
    }
  };

  // Auto-fill credits
  const autoFillCredits = useCallback(() => {
    if (!creditSystemEnabled || !creditRules || !selectedAcademicYear) return;
    
    const totalDegreeCredits = creditRules.academicSystem?.totalDegreeCredits || 140;
    
    if (!isSemesterBased) {
      setTotalCredits(totalDegreeCredits);
      setAutoFilledCredits(true);
    } else if (creditSummary) {
      const recommended = getRecommendedCredits();
      if (recommended > 0) {
        setTotalCredits(recommended);
        setAutoFilledCredits(true);
      }
    }
  }, [creditSystemEnabled, creditRules, selectedAcademicYear, isSemesterBased, creditSummary]);

  // Reset form when modal opens - UPDATED to handle term
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      fetchCreditSystemRules();
      
      if (isEditMode && existingMapping) {
        initializeExistingMapping();
      } else {
        const hasDeptSelection = Array.isArray(selectedDepartments) && selectedDepartments.length > 0;
        const hasSubjectsLoaded = Object.keys(departmentSubjects || {}).length > 0;
        const hasSelectedSubjects = Object.keys(selectedSubjects || {}).some(k => (selectedSubjects[k] || []).length > 0);

        if (!hasDeptSelection && !hasSubjectsLoaded && !hasSelectedSubjects) {
          if (academicYearId) {
            setSelectedAcademicYear(academicYearId);
            setSelectedSemester(semesterId || '');
            setSelectedTerm(termId || ''); // ADDED: set selectedTerm from props
            setUseParentContext(true);
          } else {
            setSelectedAcademicYear('');
            setSelectedSemester('');
            setSelectedTerm(''); // ADDED: reset selectedTerm
            setUseParentContext(false);
          }
          setSelectedDepartments([]);
          setActiveDepartment('');
          setSelectedSubjects({});
          setDepartmentSubjects({});
          setSubjectsLoading({});
          setSubjectsError({});
          setErrors({});
          setTotalCredits(0);
          setCreditSummary(null);
          setCreditValidation({ valid: true, warnings: [] });
          setSubjectSearch('');
          setAutoExpandedCategories(false);
          setAutoFilledCredits(false);
        } else {
          if (!selectedAcademicYear && academicYearId) {
            setSelectedAcademicYear(academicYearId);
            setUseParentContext(true);
            setSelectedSemester(semesterId || '');
            setSelectedTerm(termId || ''); // ADDED: set selectedTerm from props
          }
        }
      }
    }
    prevOpen.current = open;
    if (!open) {
      hasInitializedEdit.current = false;
      editModeInitialized.current = false;
      setShowConfirmation(false); 
    }
  }, [open, academicYearId, semesterId, termId, isEditMode, existingMapping, selectedDepartments.length, departmentSubjects, selectedSubjects, fetchCreditSystemRules, initializeExistingMapping]);

  // Fetch subjects when academic context changes (add mode)
  useEffect(() => {
    if (
      open &&
      !isEditMode &&
      !isInitializingEdit.current &&
      selectedDepartments.length > 0 &&
      selectedAcademicYear
    ) {
      const departmentsNeedingFetch = selectedDepartments.filter(
        deptId => !departmentSubjects[deptId] && !subjectsLoading[deptId]
      );
      
      if (departmentsNeedingFetch.length > 0) {
        departmentsNeedingFetch.forEach(deptId => {
          fetchSubjectsForDepartment(deptId);
        });
      }
    }
  }, [open, isEditMode, selectedDepartments, selectedAcademicYear, departmentSubjects, subjectsLoading, fetchSubjectsForDepartment]);

  // Auto-fill credits effect
  useEffect(() => {
    if (open && selectedAcademicYear && creditSystemEnabled && creditRules && !autoFilledCredits && !isEditMode) {
      const timer = setTimeout(() => {
        autoFillCredits();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [open, selectedAcademicYear, creditSystemEnabled, creditRules, autoFilledCredits, autoFillCredits, isEditMode]);

  // Fetch credit summary effect
  useEffect(() => {
    if (open && selectedAcademicYear && creditSystemEnabled) {
      fetchCreditSummary();
    }
  }, [open, selectedAcademicYear, creditSystemEnabled, fetchCreditSummary]);

  // Validate credits effect
  useEffect(() => {
    if (creditSystemEnabled && totalCredits > 0) {
      const timeoutId = setTimeout(() => {
        validateCredits(totalCredits);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [totalCredits, creditSystemEnabled, validateCredits]);

  // Initialize academic context effect - UPDATED to fetch terms
  useEffect(() => {
    if (open && selectedAcademicYear && academicYears.length > 0) {
      const selectedAY = academicYears.find(ay => ay._id === selectedAcademicYear);
      if (selectedAY) {
        setIsSemesterBased(selectedAY.isSemesterBased || false);
        if (selectedAY.isSemesterBased) {
          fetchSemestersForAcademicYear(selectedAcademicYear);
          setTerms([]); // Clear terms
        } else {
          fetchTermsForAcademicYear(selectedAcademicYear); // NEW: Fetch terms
          setSemesters([]); // Clear semesters
          setSelectedSemester(''); // Clear semester selection
        }
      }
    }
  }, [open, selectedAcademicYear, academicYears]);

  // Update context effect - UPDATED to fetch terms
  useEffect(() => {
    if (selectedAcademicYear && !useParentContext) {
      const selectedAY = academicYears.find(ay => ay._id === selectedAcademicYear);
      if (selectedAY) {
        setIsSemesterBased(selectedAY.isSemesterBased || false);
        if (selectedAY.isSemesterBased) {
          fetchSemestersForAcademicYear(selectedAcademicYear);
          setTerms([]);
        } else {
          fetchTermsForAcademicYear(selectedAcademicYear); // NEW: Fetch terms
          setSemesters([]);
          setSelectedSemester('');
        }
      }
    }
  }, [selectedAcademicYear, academicYears, useParentContext]);

  // Fetch semesters
  const fetchSemestersForAcademicYear = async (academicYearId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/classManager/semesters/${academicYearId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const fetchedSemesters = res.data.semesters || [];
      setSemesters(fetchedSemesters);
      if (fetchedSemesters.length > 0 && !selectedSemester) {
        const firstSemester = fetchedSemesters[0]._id || fetchedSemesters[0].id;
        setSelectedSemester(firstSemester);
      }
    } catch (err) {
      setSemesters([]);
    }
  };

  // NEW: Fetch terms for academic year
  const fetchTermsForAcademicYear = async (academicYearId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/academic-year/${academicYearId}/terms?tenantId=${tenantId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const fetchedTerms = res.data?.terms || [];
      setTerms(fetchedTerms);
      if (fetchedTerms.length > 0 && !selectedTerm) {
        const firstTerm = fetchedTerms[0]._id || fetchedTerms[0].id;
        setSelectedTerm(firstTerm);
      }
    } catch (err) {
      setTerms([]);
    }
  };

  // Recommended credits helper
  const getRecommendedCredits = () => {
    if (!creditSummary || !creditSystemEnabled) return 0;
    const remainingCredits = Math.max(creditSummary.remainingCredits, 0);
    const remainingSemesters = creditSummary.remainingSemesters;
    if (remainingSemesters <= 0) return 0;
    return Math.floor(remainingCredits / remainingSemesters);
  };

  const autoFillRecommendedCredits = () => {
    const recommended = getRecommendedCredits();
    if (recommended > 0) {
      setTotalCredits(recommended);
      setAutoFilledCredits(true);
    }
  };

  // Context mode toggle - UPDATED to handle term
  const toggleContextMode = () => {
    const newMode = !useParentContext;
    setUseParentContext(newMode);
    if (newMode && academicYearId) {
      setSelectedAcademicYear(academicYearId);
      setSelectedSemester(semesterId || '');
      setSelectedTerm(termId || ''); // ADDED
    } else {
      setSelectedAcademicYear('');
      setSelectedSemester('');
      setSelectedTerm(''); // ADDED
    }
    setSelectedDepartments([]);
    setSelectedSubjects({});
    setDepartmentSubjects({});
  };

  // Sync selected subjects
  useEffect(() => {
    const newSelectedSubjects = { ...selectedSubjects };
    selectedDepartments.forEach(deptId => {
      if (!newSelectedSubjects[deptId]) {
        newSelectedSubjects[deptId] = [];
      }
    });
    Object.keys(newSelectedSubjects).forEach(deptId => {
      if (!selectedDepartments.includes(deptId)) {
        delete newSelectedSubjects[deptId];
      }
    });
    setSelectedSubjects(newSelectedSubjects);
  }, [selectedDepartments]);

  // Department selection
  const toggleDepartment = (deptId) => {
    setSelectedDepartments(prev => {
      if (prev.includes(deptId)) {
        const newSelected = prev.filter(id => id !== deptId);
        setSelectedSubjects(prevSubjects => {
          const newSubjects = { ...prevSubjects };
          delete newSubjects[deptId];
          return newSubjects;
        });
        if (activeDepartment === deptId) {
          setActiveDepartment(newSelected[0] || '');
        }
        return newSelected;
      } else {
        const newSelected = [...prev, deptId];
        if (!activeDepartment) {
          setActiveDepartment(deptId);
        }
        return newSelected;
      }
    });
  };

  const selectAllDepartments = () => {
    const allDeptIds = allDepartments.map(dept => dept._id);
    setSelectedDepartments(allDeptIds);
    if (!activeDepartment && allDeptIds.length > 0) {
      setActiveDepartment(allDeptIds[0]);
    }
  };

  const clearAllDepartments = () => {
    setSelectedDepartments([]);
    setActiveDepartment('');
    setSelectedSubjects({});
    setDepartmentSubjects({});
  };

  const fetchDepartmentSubjects = (deptId) => {
    if (selectedAcademicYear) {
      fetchSubjectsForDepartment(deptId);
    }
  };

  // Subject selection
  const toggleSubject = (deptId, subjectId) => {
    setSelectedSubjects(prev => {
      const currentSubjects = prev[deptId] || [];
      const isSelected = currentSubjects.includes(subjectId);
      let newSubjects;
      if (isSelected) {
        newSubjects = currentSubjects.filter(id => id !== subjectId);
      } else {
        newSubjects = [...currentSubjects, subjectId];
      }
      return {
        ...prev,
        [deptId]: newSubjects
      };
    });
  };

  const selectAllInDepartment = (deptId) => {
    const deptSubjects = departmentSubjects[deptId] || [];
    const departmentSubjectIds = deptSubjects.map(subj => subj._id);
    const currentSubjects = selectedSubjects[deptId] || [];
    const allSelected = departmentSubjectIds.every(id => currentSubjects.includes(id));
    const newSubjects = allSelected ? [] : departmentSubjectIds;
    setSelectedSubjects(prev => ({
      ...prev,
      [deptId]: newSubjects
    }));
  };

  // Get active subjects with deduplication
  const getActiveDepartmentSubjects = () => {
    const rawSubjects = departmentSubjects[activeDepartment] || [];
    const currentSelectedIds = selectedSubjects[activeDepartment] || [];
    const uniqueMap = new Map();

    rawSubjects.forEach(subj => {
      const key = subj.subjectCode 
        ? subj.subjectCode.toLowerCase().trim() 
        : (subj.name ? subj.name.toLowerCase().trim() : subj._id);

      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, subj);
      } else {
        const existing = uniqueMap.get(key);
        const isExistingSelected = currentSelectedIds.includes(existing._id);
        const isNewSelected = currentSelectedIds.includes(subj._id);
        if (isNewSelected && !isExistingSelected) {
          uniqueMap.set(key, subj);
        }
      }
    });

    return Array.from(uniqueMap.values());
  };

  // Categorize subjects
  const categorizedSubjects = React.useMemo(() => {
    const activeDeptSubjects = getActiveDepartmentSubjects();
    const categories = {
      core: { title: 'Core Subjects', description: 'Mandatory academic subjects', icon: '📚', subjects: [], color: 'blue' },
      laboratory: { title: 'Laboratory Subjects', description: 'Practical and lab-based subjects', icon: '🔬', subjects: [], color: 'purple' },
      language: { title: 'Language Subjects', description: 'Language and communication subjects', icon: '🌐', subjects: [], color: 'green' },
      elective: { title: 'Elective Subjects', description: 'Optional subjects for specialization', icon: '🎯', subjects: [], color: 'orange' },
      project: { title: 'Project Work', description: 'Research and project-based subjects', icon: '💼', subjects: [], color: 'indigo' },
      other: { title: 'Other Subjects', description: 'Additional subject categories', icon: '📝', subjects: [], color: 'gray' }
    };

    const searchLower = subjectSearch.toLowerCase();
    const seenSubjects = new Set();
    
    activeDeptSubjects.forEach(subject => {
      if (seenSubjects.has(subject._id)) return;
      seenSubjects.add(subject._id);

      if (subjectSearch && 
          !subject.name.toLowerCase().includes(searchLower) &&
          !subject.subjectCode.toLowerCase().includes(searchLower)) {
        return;
      }

      let category = 'other';
      if (subject.isLab) category = 'laboratory';
      else if (subject.isLanguage) category = 'language';
      else if (subject.isElective) category = 'elective';
      else if (subject.isProject) category = 'project';
      else if (subject.subjectType === 'theory' || !subject.isElective) category = 'core';

      categories[category].subjects.push(subject);
    });

    return Object.fromEntries(
      Object.entries(categories).filter(([_, category]) => category.subjects.length > 0)
    );
  }, [activeDepartment, departmentSubjects, subjectSearch, selectedSubjects]);

  const isSubjectSelected = (deptId, subjectId) => {
    return selectedSubjects[deptId]?.includes(subjectId) || false;
  };

  // UI helpers
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const expandAllCategories = () => {
    const allExpanded = {};
    Object.keys(categorizedSubjects).forEach(category => {
      allExpanded[category] = true;
    });
    setExpandedCategories(allExpanded);
  };

  const collapseAllCategories = () => {
    const allCollapsed = {};
    Object.keys(categorizedSubjects).forEach(category => {
      allCollapsed[category] = false;
    });
    setExpandedCategories(allCollapsed);
  };

  const getDepartmentName = (deptId) => {
    const dept = allDepartments.find(d => d._id === deptId);
    return dept?.name || 'Unknown Department';
  };

  const getTotalSelectedSubjects = () => {
    return Object.values(selectedSubjects).reduce((total, subjects) => total + subjects.length, 0);
  };

  const getDepartmentSubjectCount = (deptId) => {
    return (selectedSubjects[deptId] || []).length;
  };

  const areAllSubjectsInCategorySelected = (deptId, category) => {
    const categorySubjects = categorizedSubjects[category]?.subjects || [];
    const currentSubjects = selectedSubjects[deptId] || [];
    if (categorySubjects.length === 0) return false;
    return categorySubjects.every(subj => currentSubjects.includes(subj._id));
  };

  const areSomeSubjectsInCategorySelected = (deptId, category) => {
    const categorySubjects = categorizedSubjects[category]?.subjects || [];
    const currentSubjects = selectedSubjects[deptId] || [];
    if (categorySubjects.length === 0) return false;
    return categorySubjects.some(subj => currentSubjects.includes(subj._id)) &&
      !categorySubjects.every(subj => currentSubjects.includes(subj._id));
  };

  const selectAllInCategory = (deptId, category) => {
    const categorySubjects = categorizedSubjects[category]?.subjects || [];
    const categorySubjectIds = categorySubjects.map(subj => subj._id);
    const currentSubjects = selectedSubjects[deptId] || [];
    const allSelected = categorySubjectIds.every(id => currentSubjects.includes(id));
    const newSubjects = allSelected
      ? currentSubjects.filter(id => !categorySubjectIds.includes(id))
      : [...new Set([...currentSubjects, ...categorySubjectIds])];
    setSelectedSubjects(prev => ({
      ...prev,
      [deptId]: newSubjects
    }));
  };

  const isAnyDepartmentLoading = () => {
    return selectedDepartments.some(deptId => subjectsLoading[deptId]);
  };

  const getDepartmentErrors = () => {
    return selectedDepartments
      .filter(deptId => subjectsError[deptId])
      .map(deptId => ({
        department: getDepartmentName(deptId),
        error: subjectsError[deptId]
      }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedAcademicYear) newErrors.academicYear = 'Academic year is required';
    if (isSemesterBased && !selectedSemester) newErrors.semester = 'Semester is required for semester-based academic years';
    if (!isSemesterBased && !selectedTerm) newErrors.term = 'Term is required for term-based academic years'; // NEW
    if (selectedDepartments.length === 0) newErrors.departments = 'At least one department is required';
    if (getTotalSelectedSubjects() === 0) newErrors.subjects = 'Select at least one subject across departments';
    if (creditSystemEnabled) {
      if (!totalCredits || totalCredits <= 0) {
        newErrors.totalCredits = 'Total credits are required when credit system is enabled';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 1. Initial Submit Click: Validate and Open Confirmation Modal
  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      const firstErrorElement = document.querySelector('.error-field');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setShowConfirmation(true);
  };

  // 2. Final Submit: Actual API Call - UPDATED to include termId
  const handleFinalSubmit = async () => {
    setShowConfirmation(false);
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const departmentSubjectsData = Object.entries(selectedSubjects)
        .filter(([deptId]) => selectedDepartments.includes(deptId))
        .map(([deptId, subjectIds]) => ({
          departmentId: deptId,
          subjects: subjectIds.map(subjectId => ({
            subjectId,
            isMandatory: true,
            teachingHours: 0
          }))
        }));

      const programTypeId = typeof program.programTypeId === 'object' ? program.programTypeId._id : program.programTypeId;

      const bulkData = {
        tenantId,
        programId: program._id,
        programTypeId: programTypeId,
        academicYearId: selectedAcademicYear,
        departmentSubjects: departmentSubjectsData,
        totalCredits: creditSystemEnabled ? totalCredits : 0,
        creditSystemEnabled: creditSystemEnabled
      };
      
      // Add appropriate context parameter based on academic year type
      if (isSemesterBased && selectedSemester) {
        bulkData.semesterId = selectedSemester;
      } else if (!isSemesterBased && selectedTerm) {
        bulkData.termId = selectedTerm; // ADDED: Include termId for non-semester-based
      }

      let response;
      if (isEditMode && existingMapping) {
        response = await api.put(`/api/program-subject-mappings/grouped/${existingMapping._id}`, bulkData, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      } else {
        response = await api.post('/api/program-subject-mappings/bulk-grouped', bulkData, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }

      const action = isEditMode ? 'updated' : 'created';
      
      // Check for class sync results in response
      const classSync = response.data?.classSync;
      
      // Build success message with sync info
      let successMessage = `Successfully ${action} mapping with ${getTotalSelectedSubjects()} subjects to program ${program.name}!`;
      
      if (classSync && classSync.triggered) {
        if (classSync.success && classSync.classesUpdated > 0) {
          successMessage += ` 🔄 ${classSync.classesUpdated} class(es) automatically synced.`;
          
          // Show detailed sync info
          if (classSync.subjectsAdded > 0 || classSync.subjectsRemoved > 0) {
            const syncDetails = [];
            if (classSync.subjectsAdded > 0) {
              syncDetails.push(`${classSync.subjectsAdded} subject(s) added`);
            }
            if (classSync.subjectsRemoved > 0) {
              syncDetails.push(`${classSync.subjectsRemoved} subject(s) removed`);
            }
            successMessage += ` (${syncDetails.join(', ')})`;
          }
          
          // Show success toast for sync
          toast.success(successMessage, {
            autoClose: 5000,
            icon: '🎉'
          });
          
          // Show additional info toast for class sync
          toast.info(
            `Class Sync Complete: ${classSync.classesUpdated} class(es) updated with the new program mapping.`,
            {
              autoClose: 4000,
              icon: '📚'
            }
          );
        } else if (classSync.skipped) {
          // Sync was skipped
          toast.success(successMessage);
          toast.warning(
            `Class sync skipped: ${classSync.skipReason || 'Academic year or semester not active'}`,
            {
              autoClose: 4000,
              icon: '⚠️'
            }
          );
        } else if (classSync.classesUpdated === 0) {
          // No classes needed updating
          toast.success(successMessage);
          toast.info(
            'No classes required updates - all classes are already in sync.',
            {
              autoClose: 3000,
              icon: 'ℹ️'
            }
          );
        } else if (!classSync.success) {
          // Sync failed
          toast.success(successMessage);
          toast.error(
            `Class sync failed: ${classSync.errors?.[0]?.error || 'Unknown error'}. Please sync manually.`,
            {
              autoClose: 5000,
              icon: '❌'
            }
          );
        }
      } else {
        // No sync info in response (for create mode or old API)
        toast.success(successMessage);
      }

      // Log sync details for debugging
      if (classSync) {
        console.log('📊 Class Sync Results:', classSync);
      }
      
      if (onMappingComplete) {
        onMappingComplete();
      }
      onClose();
      
    } catch (err) {
      if (err.response?.status === 400) {
        const errorMessage = err.response?.data?.message || 'Validation failed';
        toast.error(errorMessage);
      } else {
        const errorMessage = err.response?.data?.message || 
                            err.response?.data?.error || 
                            'Error saving subject mapping';
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      gray: 'bg-gray-50 border-gray-200 text-gray-800'
    };
    return colorMap[color] || colorMap.gray;
  };

  const getBadgeColor = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
      green: 'bg-green-100 text-green-700 border-green-200',
      orange: 'bg-orange-100 text-orange-700 border-orange-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      gray: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colorMap[color] || colorMap.gray;
  };

  const getCreditWarningLevel = () => {
    const hasWarnings = creditValidation.warnings.length > 0;
    return hasWarnings ? 'advisory' : 'success';
  };

  const isMappingEnabled = () => {
    if (submitting) return false;
    if (!selectedAcademicYear) return false;
    if (isSemesterBased && !selectedSemester) return false;
    if (!isSemesterBased && !selectedTerm) return false; // NEW
    if (selectedDepartments.length === 0) return false;
    if (getTotalSelectedSubjects() === 0) return false;
    if (creditSystemEnabled && (!totalCredits || totalCredits <= 0)) return false;
    return true;
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
          {/* Header - Fixed */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white p-6 flex-shrink-0">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">
                  {isEditMode ? 'Edit Program Mapping' : 'Map Subjects to Program'}
                </h2>
                <p className="text-purple-100 opacity-90">
                  {program ? `${isEditMode ? 'Editing' : 'Mapping'} subjects for: ${program.name}` : 'Select a program to map subjects'}
                  {getTotalSelectedSubjects() > 0 && (
                    <span className="ml-2 bg-white bg-opacity-20 px-2 py-1 rounded-full text-sm">
                      {getTotalSelectedSubjects()} subject(s) selected
                      {creditSystemEnabled && ` • ${totalCredits} total credits`}
                    </span>
                  )}
                </p>
                
                {/* Credit System Indicator */}
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex items-center gap-2 text-purple-200 text-sm">
                    {creditSystemEnabled ? (
                      <>
                        <FiCreditCard className="w-4 h-4 text-green-300" />
                        <span className="text-green-300 font-medium">Credit System Enabled</span>
                        {!isSemesterBased && (
                          <span className="ml-2 bg-green-500 bg-opacity-20 px-2 py-1 rounded-full text-xs">
                            Annual/Term System - Auto-filled credits
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <FiCreditCard className="w-4 h-4 text-yellow-300" />
                        <span className="text-yellow-300">Credit System Disabled</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-purple-200 transition-colors duration-200 p-1 rounded-lg flex-shrink-0 ml-4"
                disabled={submitting}
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {!program ? (
              <div className="text-center py-12">
                <FiBook className="mx-auto text-4xl text-gray-300 mb-3" />
                <p className="text-gray-600 text-lg">Please select a program to map subjects.</p>
              </div>
            ) : (
              <form onSubmit={handlePreSubmit} className="space-y-6">
                {/* Academic Context Section - UPDATED to include term selection */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FiCalendar className="text-purple-600" />
                      Academic Context
                    </h3>
                    {!isEditMode && (
                      <button
                        type="button"
                        onClick={toggleContextMode}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors"
                      >
                        {useParentContext ? (
                          <>
                            <FiEdit3 className="w-3 h-3" />
                            Switch to Free Selection
                          </>
                        ) : (
                          <>
                            <FiCopy className="w-3 h-3" />
                            Use Parent Context
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Academic Year */}
                    <div className={errors.academicYear ? 'error-field' : ''}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Academic Year *
                      </label>
                      <select
                        value={selectedAcademicYear}
                        onChange={(e) => setSelectedAcademicYear(e.target.value)}
                        disabled={useParentContext || isEditMode}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                          errors.academicYear ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        } ${(useParentContext || isEditMode) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        required
                      >
                        <option value="">Select Academic Year</option>
                        {academicYears.map(ay => (
                          <option key={ay._id || ay.id} value={ay._id || ay.id}>
                            {ay.name}{ay.label ? ` (${ay.label})` : ''}
                          </option>
                        ))}
                      </select>
                      {errors.academicYear && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <FiAlertCircle className="w-4 h-4" />
                          {errors.academicYear}
                        </p>
                      )}
                    </div>

                    {/* Semester Selection (Conditional) */}
                    {isSemesterBased && (
                      <div className={errors.semester ? 'error-field' : ''}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Semester *
                        </label>
                        <select
                          value={selectedSemester}
                          onChange={(e) => setSelectedSemester(e.target.value)}
                          disabled={useParentContext || isEditMode}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                            errors.semester ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          } ${(useParentContext || isEditMode) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        >
                          <option value="">Select Semester</option>
                          {semesters.map(sem => (
                            <option key={sem._id || sem.id} value={sem._id || sem.id}>
                              {sem.semesterName || `Semester ${sem.semesterNumber}`}
                            </option>
                          ))}
                        </select>
                        {errors.semester && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <FiAlertCircle className="w-4 h-4" />
                            {errors.semester}
                          </p>
                        )}
                      </div>
                    )}

                    {/* NEW: Term Selection (Conditional for non-semester-based) */}
                    {!isSemesterBased && (
                      <div className={errors.term ? 'error-field' : ''}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="flex items-center gap-1">
                            <FiList className="w-4 h-4" />
                            Term *
                          </span>
                        </label>
                        <select
                          value={selectedTerm}
                          onChange={(e) => setSelectedTerm(e.target.value)}
                          disabled={useParentContext || isEditMode}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                            errors.term ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          } ${(useParentContext || isEditMode) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        >
                          <option value="">Select Term</option>
                          {terms.map(term => (
                            <option key={term._id || term.id} value={term._id || term.id}>
                              {term.termLabel || `Term ${term.termNumber}`}
                            </option>
                          ))}
                        </select>
                        {errors.term && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <FiAlertCircle className="w-4 h-4" />
                            {errors.term}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Current Selection Display */}
                    <div className="flex items-end">
                      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded border w-full">
                        <strong>Current Context:</strong><br />
                        {selectedAcademicYear ? 
                          (() => {
                            const ay = academicYears.find(ay => ay._id === selectedAcademicYear);
                            return ay ? `${ay.name}${ay.label ? ` (${ay.label})` : ''}` : 'Not selected';
                          })()
                          : 'Not selected'
                        } 
                        {isSemesterBased && selectedSemester && 
                          ` → ${semesters.find(s => s._id === selectedSemester)?.semesterName || 'Selected semester'}`
                        }
                        {!isSemesterBased && selectedTerm && 
                          ` → ${terms.find(t => t._id === selectedTerm)?.termLabel || 'Selected term'}`
                        }
                        {!isSemesterBased && !selectedTerm && selectedAcademicYear && ' → Select term'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Credit Summary Section */}
                {creditSystemEnabled && selectedAcademicYear && (
                  <div className={`border rounded-xl p-6 ${
                    creditSummary?.utilizationPercentage >= 90 
                      ? 'bg-orange-50 border-orange-200' 
                      : creditSummary?.utilizationPercentage >= 75
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-semibold flex items-center gap-2 ${
                        creditSummary?.utilizationPercentage >= 90 
                          ? 'text-orange-900' 
                          : creditSummary?.utilizationPercentage >= 75
                          ? 'text-yellow-900'
                          : 'text-blue-900'
                      }`}>
                        <FiPieChart className={
                          creditSummary?.utilizationPercentage >= 90 
                            ? 'text-orange-600' 
                            : creditSummary?.utilizationPercentage >= 75
                            ? 'text-yellow-600'
                            : 'text-blue-600'
                        } />
                        Credit Distribution Summary
                      </h3>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={fetchCreditSummary}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-200 text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors"
                        >
                          <FiRefreshCw className="w-3 h-3" />
                          Refresh
                        </button>
                      </div>
                    </div>

                    {creditSummary ? (
                      <div className="space-y-4">
                        {/* Progress Bar */}
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className={
                              creditSummary.utilizationPercentage >= 90 
                                ? 'text-orange-800' 
                                : creditSummary.utilizationPercentage >= 75
                                ? 'text-yellow-800'
                                : 'text-blue-800'
                            }>
                              Degree Progress
                            </span>
                            <span className={
                              creditSummary.utilizationPercentage >= 90 
                                ? 'text-orange-800' 
                                : creditSummary.utilizationPercentage >= 75
                                ? 'text-yellow-800'
                                : 'text-blue-800'
                            }>
                              {creditSummary.totalCreditsAssigned} / {creditSummary.totalDegreeCredits} credits
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full transition-all duration-500 ${
                                creditSummary.utilizationPercentage >= 90 
                                  ? 'bg-orange-500' 
                                  : creditSummary.utilizationPercentage >= 75
                                  ? 'bg-yellow-500'
                                  : 'bg-blue-600'
                              }`}
                              style={{ width: `${Math.min(creditSummary.utilizationPercentage, 100)}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-xs mt-1">
                            <span className={
                              creditSummary.utilizationPercentage >= 90 
                                ? 'text-orange-700' 
                                : creditSummary.utilizationPercentage >= 75
                                ? 'text-yellow-700'
                                : 'text-blue-700'
                            }>
                              {creditSummary.utilizationPercentage}% utilized
                            </span>
                            <span className={
                              creditSummary.utilizationPercentage >= 90 
                                ? 'text-orange-700' 
                                : creditSummary.utilizationPercentage >= 75
                                ? 'text-yellow-700'
                                : 'text-blue-700'
                            }>
                              {Math.max(creditSummary.remainingCredits, 0)} credits remaining
                            </span>
                          </div>
                        </div>

                        {/* System Information */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="text-center p-2 bg-white rounded border">
                            <div className="font-semibold text-gray-900">{creditSummary.totalSemesters}</div>
                            <div className="text-gray-600 text-xs">Total Semesters</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded border">
                            <div className="font-semibold text-gray-900">{creditSummary.recommendedPerSemester}</div>
                            <div className="text-gray-600 text-xs">Avg/Semester</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded border">
                            <div className="font-semibold text-gray-900">{creditSummary.remainingSemesters}</div>
                            <div className="text-gray-600 text-xs">Remaining Semesters</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded border">
                            <div className="font-semibold text-gray-900">{creditSummary.averagePerRemaining}</div>
                            <div className="text-gray-600 text-xs">Avg/Remaining</div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-blue-700">
                        <FiInfo className="mx-auto w-8 h-8 mb-2 opacity-50" />
                        <p>No credit data available for this academic year.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Total Credits Section */}
                {creditSystemEnabled && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FiCreditCard className="text-green-600" />
                        Program Credits
                        {autoFilledCredits && (
                          <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                            Auto-filled
                          </span>
                        )}
                      </h3>
                      <div className="flex gap-2">
                        {getRecommendedCredits() > 0 && !isEditMode && (
                          <button
                            type="button"
                            onClick={autoFillRecommendedCredits}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-green-200 text-green-700 bg-green-100 hover:bg-green-200 transition-colors"
                          >
                            <FiPlus className="w-3 h-3" />
                            Auto-fill Recommended ({getRecommendedCredits()} credits)
                          </button>
                        )}
                        {!isSemesterBased && (
                          <button
                            type="button"
                            onClick={() => {
                              const totalDegreeCredits = creditRules?.academicSystem?.totalDegreeCredits || 140;
                              setTotalCredits(totalDegreeCredits);
                              setAutoFilledCredits(true);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-200 text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors"
                          >
                            <FiRefreshCw className="w-3 h-3" />
                            Reset to Degree Total
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="max-w-md">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Credits for this {isSemesterBased ? 'Semester' : 'Academic Year'} *
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          name="totalCredits"
                          value={totalCredits}
                          onChange={(e) => handleCreditChange(e.target.value)}
                          className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                            errors.totalCredits ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          } ${validatingCredits ? 'opacity-50' : ''}`}
                          placeholder={`Enter total credits`}
                          required={creditSystemEnabled}
                          disabled={validatingCredits}
                        />
                        <span className="text-sm text-gray-500 whitespace-nowrap">
                          credits
                        </span>
                      </div>
                      
                      {validatingCredits && (
                        <div className="mt-2 flex items-center gap-2 text-blue-600 text-sm">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                          Validating credits...
                        </div>
                      )}

                      {!validatingCredits && creditValidation.warnings.length > 0 && (
                        <div className={`mt-2 space-y-2 ${
                          getCreditWarningLevel() === 'advisory' ? 'text-orange-600' : 'text-blue-600'
                        }`}>
                          {creditValidation.warnings.map((warning, index) => (
                            <div key={index} className={`flex items-start gap-2 text-sm p-2 rounded-lg ${
                              warning.type === 'advisory' ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'
                            }`}>
                              {warning.type === 'advisory' ? (
                                <FiAlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              ) : (
                                <FiInfo className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              )}
                              <div>
                                <span className="font-medium">
                                  {warning.type === 'advisory' ? 'Advisory: ' : 'Info: '}
                                </span>
                                <span>{warning.message}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {errors.totalCredits && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <FiAlertCircle className="w-4 h-4" />
                          {errors.totalCredits}
                        </p>
                      )}

                      <div className="mt-2 text-xs text-gray-500">
                        {!isSemesterBased ? (
                          <p>💡 Annual/Term system: Typically uses full degree credits ({creditRules?.academicSystem?.totalDegreeCredits || 140})</p>
                        ) : (
                          <p>💡 Semester system: Recommended {getRecommendedCredits()} credits for balanced distribution</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Department Selection Section */}
                {selectedAcademicYear && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FiUsers className="text-blue-600" />
                        Department Selection
                      </h3>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={selectAllDepartments}
                          className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={clearAllDepartments}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Clear All
                        </button>
                        {selectedDepartments.length > 0 && (
                          <button
                            type="button"
                            onClick={fetchAllDepartmentSubjects}
                            className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 border border-green-200 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-1"
                          >
                            <FiRefreshCw className="w-3 h-3" />
                            Refresh Subjects
                          </button>
                        )}
                      </div>
                    </div>

                    {errors.departments && (
                      <p className="text-sm text-red-600 mb-4 flex items-center gap-1">
                        <FiAlertCircle className="w-4 h-4" />
                        {errors.departments}
                      </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                      {allDepartments.map(dept => (
                        <label key={dept._id} className="flex items-center space-x-2 p-3 hover:bg-gray-50 rounded border border-gray-200 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedDepartments.includes(dept._id)}
                            onChange={() => toggleDepartment(dept._id)}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-900 flex-1">{dept.name}</span>
                          {selectedSubjects[dept._id] && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full flex-shrink-0">
                              {getDepartmentSubjectCount(dept._id)} selected
                            </span>
                          )}
                          {subjectsLoading[dept._id] && (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 flex-shrink-0"></div>
                          )}
                          {selectedDepartments.includes(dept._id) && !departmentSubjects[dept._id]?.length && !subjectsLoading[dept._id] && (
                            <button
                              type="button"
                              onClick={() => fetchDepartmentSubjects(dept._id)}
                              className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded hover:bg-green-200 transition-colors flex-shrink-0"
                              title="Load subjects"
                            >
                              Load
                            </button>
                          )}
                        </label>
                      ))}
                    </div>

                    <div className="text-xs text-gray-500 mt-2">
                      {selectedDepartments.length} department(s) selected • {getTotalSelectedSubjects()} total subjects selected
                      {isAnyDepartmentLoading() && (
                        <span className="ml-2 text-blue-600">
                          • Loading subjects...
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Subject Selection Section */}
                {selectedDepartments.length > 0 && selectedAcademicYear && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <FiBook className="text-green-600" />
                          Subject Selection
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {getTotalSelectedSubjects()} subject(s) selected across {selectedDepartments.length} department(s)
                          {creditSystemEnabled && ` • ${totalCredits} total credits`}
                        </p>
                        {errors.subjects && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <FiAlertCircle className="w-4 h-4" />
                            {errors.subjects}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        {/* Search Input */}
                        <div className="relative">
                          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search subjects..."
                            value={subjectSearch}
                            onChange={(e) => setSubjectSearch(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 w-48"
                          />
                        </div>
                        
                        <div className="relative">
                          <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <select
                            value={subjectFilter}
                            onChange={(e) => setSubjectFilter(e.target.value)}
                            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white"
                          >
                            <option value="all">All Subjects</option>
                            <option value="selected">Selected Only</option>
                            {Object.keys(categorizedSubjects).map(category => (
                              <option key={category} value={category}>
                                {categorizedSubjects[category].title}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Expand/Collapse All */}
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={expandAllCategories}
                            className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Expand All
                          </button>
                          <button
                            type="button"
                            onClick={collapseAllCategories}
                            className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Collapse All
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Department Errors */}
                    {getDepartmentErrors().length > 0 && (
                      <div className="mb-4 space-y-2">
                        {getDepartmentErrors().map((deptError, index) => (
                          <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                              <FiAlertCircle className="text-red-500 flex-shrink-0" />
                              <div>
                                <p className="text-red-800 text-sm">
                                  <strong>{deptError.department}:</strong> {deptError.error}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {isAnyDepartmentLoading() && (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                        <span className="ml-3 text-gray-600">Loading subjects for {selectedDepartments.length} department(s)...</span>
                      </div>
                    )}

                    {!isAnyDepartmentLoading() && selectedDepartments.length > 0 && (
                      <div className="space-y-6">
                        <div className="border-b border-gray-200">
                          <nav className="flex space-x-8 overflow-x-auto">
                            {selectedDepartments.map((deptId) => (
                              <button
                                key={deptId}
                                type="button"
                                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                                  activeDepartment === deptId
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                                onClick={() => setActiveDepartment(deptId)}
                              >
                                {getDepartmentName(deptId)}
                                <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
                                  activeDepartment === deptId
                                    ? 'bg-blue-100 text-blue-600'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {getDepartmentSubjectCount(deptId)}
                                </span>
                                {subjectsLoading[deptId] && (
                                  <div className="ml-1 animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 inline-block"></div>
                                )}
                              </button>
                            ))}
                          </nav>
                        </div>

                        {/* Now, show either the subject selection UI or a "No subjects" message */}
                        {activeDepartment && departmentSubjects[activeDepartment]?.length > 0 ? (
                          <div>
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                  <h4 className="font-semibold text-blue-900">
                                    {getDepartmentName(activeDepartment)} - Subject Selection
                                  </h4>
                                  <p className="text-blue-700 text-sm">
                                    Select subjects to map to the program
                                    {subjectSearch && (
                                      <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                        Searching: "{subjectSearch}"
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-blue-700 text-sm whitespace-nowrap">
                                    {getDepartmentSubjectCount(activeDepartment)} subjects selected
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => selectAllInDepartment(activeDepartment)}
                                    className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded hover:bg-blue-200 transition-colors whitespace-nowrap"
                                  >
                                    {getDepartmentSubjectCount(activeDepartment) === 
                                     (departmentSubjects[activeDepartment] || []).length
                                      ? 'Clear All'
                                      : 'Select All'
                                    }
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-4">
                              {Object.entries(categorizedSubjects).map(([categoryKey, category]) => {
                                const isExpanded = expandedCategories[categoryKey] !== false;
                                const allSelected = areAllSubjectsInCategorySelected(activeDepartment, categoryKey);
                                const someSelected = areSomeSubjectsInCategorySelected(activeDepartment, categoryKey);

                                if (category.subjects.length === 0) return null;

                                return (
                                  <div key={categoryKey} className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div
                                      className={`p-4 cursor-pointer transition-colors hover:bg-opacity-80 ${getColorClasses(category.color)}`}
                                      onClick={() => toggleCategory(categoryKey)}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                selectAllInCategory(activeDepartment, categoryKey);
                                              }}
                                              className="p-1 hover:bg-white hover:bg-opacity-50 rounded transition-colors"
                                              title={allSelected ? 'Deselect all' : 'Select all'}
                                            >
                                              {allSelected ? (
                                                <FiCheckSquare className="w-5 h-5" />
                                              ) : someSelected ? (
                                                <div className="w-5 h-5 border-2 border-current rounded bg-current bg-opacity-20" />
                                              ) : (
                                                <FiSquare className="w-5 h-5" />
                                              )}
                                            </button>
                                            <span className="text-lg font-semibold">{category.title}</span>
                                          </div>
                                          <span className="text-sm opacity-75">({category.subjects.length})</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          <span className="text-sm opacity-75 hidden sm:block">{category.description}</span>
                                          {isExpanded ? <FiChevronUp className="flex-shrink-0" /> : <FiChevronDown className="flex-shrink-0" />}
                                        </div>
                                      </div>
                                    </div>

                                    {isExpanded && (
                                      <div className="p-4 bg-gray-50">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                          {category.subjects
                                            .filter(subject => {
                                              if (subjectFilter === 'selected') {
                                                return isSubjectSelected(activeDepartment, subject._id);
                                              }
                                              if (subjectFilter === 'all' || subjectFilter === categoryKey) {
                                                return true;
                                              }
                                              return true;
                                            })
                                            .map((subject) => {
                                              const isSelected = isSubjectSelected(activeDepartment, subject._id);
                                              const isLab = subject.isLab ||
                                                subject.type === 'lab' ||
                                                subject.subjectType === 'lab' ||
                                                (subject.name && subject.name.toLowerCase().includes('lab'));

                                              return (
                                                <label
                                                  key={subject._id}
                                                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                                                    isSelected
                                                      ? isLab
                                                        ? 'bg-purple-50 border-purple-300 shadow-sm'
                                                        : 'bg-white border-blue-300 shadow-sm'
                                                      : 'bg-white border-gray-200 hover:border-gray-300'
                                                  }`}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    className="mt-1 mr-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                    checked={isSelected}
                                                    onChange={() => toggleSubject(activeDepartment, subject._id)}
                                                  />
                                                  <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between mb-2">
                                                      <div className="flex-1">
                                                        <div className="font-medium text-gray-900 text-sm leading-tight">
                                                          {subject.name}
                                                          {isLab && (
                                                            <span className="ml-2 text-purple-600 text-xs font-medium">🔬 Lab</span>
                                                          )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-1">
                                                          Code: {subject.subjectCode}
                                                        </div>
                                                      </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-1">
                                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getBadgeColor(category.color)}`}>
                                                        {subject.subjectType || 'theory'}
                                                      </span>

                                                      {isLab && (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                                                          Laboratory
                                                        </span>
                                                      )}

                                                      {subject.isElective && (
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                          subject.requiresGroupManagement
                                                            ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                                            : 'bg-blue-100 text-blue-700 border border-blue-200'
                                                        }`}>
                                                          {subject.requiresGroupManagement ? 'Choice Based' : 'Common'} Elective
                                                        </span>
                                                      )}

                                                      {subject.isLanguage && (
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                          subject.requiresGroupManagement
                                                            ? 'bg-green-100 text-green-700 border border-green-200'
                                                            : 'bg-teal-100 text-teal-700 border border-teal-200'
                                                        }`}>
                                                          {subject.requiresGroupManagement ? 'Choice Based' : 'Common'} Language
                                                        </span>
                                                      )}

                                                      {subject.isProject && (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200">
                                                          Project
                                                        </span>
                                                      )}
                                                    </div>
                                                  </div>
                                                </label>
                                              );
                                            })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                            <FiBook className="mx-auto text-4xl text-gray-400 mb-3" />
                            <p className="text-gray-600">
                              {subjectsError[activeDepartment]
                                ? `Failed to load subjects for ${getDepartmentName(activeDepartment)}`
                                : `No subjects loaded for ${getDepartmentName(activeDepartment)}`}
                            </p>
                            <p className="text-sm text-gray-500 mt-1 mb-4">
                              Please check the academic context or refresh subjects
                            </p>
                            <div className="flex gap-2 justify-center">
                              <button
                                type="button"
                                onClick={() => fetchDepartmentSubjects(activeDepartment)}
                                className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-lg hover:bg-green-200 transition-colors"
                              >
                                Load Subjects for {getDepartmentName(activeDepartment)}
                              </button>
                              <button
                                type="button"
                                onClick={fetchAllDepartmentSubjects}
                                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-lg hover:bg-blue-200 transition-colors"
                              >
                                Load All Subjects
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Summary Section */}
                {getTotalSelectedSubjects() > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-green-900 flex items-center gap-2">
                          <FiAward className="text-green-600" />
                          {isEditMode ? 'Update Summary' : 'Mapping Summary'}
                        </h3>
                        <p className="text-green-700 text-sm mt-1">
                          {isEditMode 
                            ? 'Ready to update program-subject mapping' 
                            : 'Ready to create program-subject mapping'
                          }
                          {creditValidation.warnings.length > 0 && (
                            <span className="ml-2 bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">
                              {creditValidation.warnings.length} advisory note(s)
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-900">
                          {getTotalSelectedSubjects()} <span className="text-lg font-normal">subjects</span>
                        </div>
                        <div className="text-green-700 text-sm">
                          across {selectedDepartments.length} departments
                          {creditSystemEnabled && ` • ${totalCredits} total credits`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Fixed Action Buttons */}
          <div className="border-t border-gray-200 bg-white p-6 flex-shrink-0">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handlePreSubmit} 
                disabled={!isMappingEnabled()}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {isEditMode ? 'Updating...' : 'Mapping Subjects...'}
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    {isEditMode ? 'Update' : 'Map'} {getTotalSelectedSubjects() || 0} Subjects
                    {creditSystemEnabled && totalCredits > 0 && ` (${totalCredits} Credits)`}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Tailwind Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-30 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full transform scale-100 animate-in zoom-in-95 duration-200 border-t-4 border-purple-600">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Confirm {isEditMode ? 'Update' : 'Mapping'}
            </h3>
            <p className="text-gray-600 mb-4">
              You are about to map <strong>{getTotalSelectedSubjects()} subjects</strong> from <strong>{selectedDepartments.length} departments</strong> to <strong>{program?.name}</strong>.
              {creditSystemEnabled && (
                <span className="block mt-2 font-medium text-purple-700">
                  Total Credits: {totalCredits}
                </span>
              )}
              {!isSemesterBased && selectedTerm && (
                <span className="block mt-1 text-sm text-purple-600">
                  Term: {terms.find(t => t._id === selectedTerm)?.termLabel || 'Selected term'}
                </span>
              )}
              {isSemesterBased && selectedSemester && (
                <span className="block mt-1 text-sm text-purple-600">
                  Semester: {semesters.find(s => s._id === selectedSemester)?.semesterName || 'Selected semester'}
                </span>
              )}
            </p>
            
            {/* Auto-sync notice for edit mode */}
            {isEditMode && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <FiLayers className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Automatic Class Sync
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      All classes linked to this program will be automatically updated with the new subject mapping.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to proceed?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFinalSubmit}
                className="px-4 py-2 bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors font-medium flex items-center gap-2 shadow-sm"
              >
                <FiCheck className="w-4 h-4" />
                Yes, {isEditMode ? 'Update' : 'Map'} Subjects
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProgramSubjectMapping;